import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Star, Briefcase, Download, LogOut, ChevronRight, Pencil, Settings } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { dispatchFetch, T, F, bd, initials } from '@/lib/dispatchApi';
import DispatchButton from '@/components/dispatch/DispatchButton';

function fmtMemberSince(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

export default function DispatchAccountPage() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [availability, setAvailability] = useState(true);
  const [togglingAvail, setTogglingAvail] = useState(false);
  const [confirmSignOut, setConfirmSignOut] = useState(false);

  const fetchProfile = useCallback(async () => {
    setError(false);
    setLoading(true);
    try {
      const res = await dispatchFetch('/api/profile');
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setProfile(data);
      setAvailability(data.availability);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);

  const toggleAvailability = async (val) => {
    setAvailability(val);
    setTogglingAvail(true);
    try {
      const res = await dispatchFetch('/api/profile', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ availability: val }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
    } catch {
      setAvailability(!val);
    } finally {
      setTogglingAvail(false);
    }
  };


  const px = 24;

  const MenuRow = ({ Icon, label, sub, onClick, last = false }) => (
    <div
      className="bx-tap"
      onClick={onClick}
      style={{ display: 'flex', alignItems: 'center', padding: '14px 16px', gap: 12, cursor: 'pointer', ...(!last ? { borderBottom: `1px solid ${T.nightHair}` } : {}) }}
    >
      <div style={{ width: 28, display: 'flex', justifyContent: 'center' }}>
        <Icon size={15} color={T.moonMute} strokeWidth={1.5} />
      </div>
      <div style={{ flex: 1 }}>
        <p style={{ fontSize: 14, color: T.moon, margin: '0 0 2px 0' }}>{label}</p>
        {sub && <p style={{ fontSize: 11, color: T.moonFaint, margin: 0 }}>{sub}</p>}
      </div>
      <ChevronRight size={14} color={T.moonFaint} strokeWidth={1.5} />
    </div>
  );

  if (error || (!loading && !profile)) {
    return (
      <div style={{ backgroundColor: T.night, minHeight: '100%', paddingLeft: px, paddingRight: px, paddingTop: 16, paddingBottom: 32, color: T.moon, fontFamily: F.heading }}>
        <p style={{ fontSize: 26, letterSpacing: -0.4, marginBottom: 28 }}>Me</p>
        <div style={{ backgroundColor: T.nightSoft, ...bd(T.nightHair), borderRadius: 2 }}>
          <MenuRow Icon={Pencil} label="Edit profile" onClick={() => navigate('/dispatch/profile-edit')} />
          <MenuRow Icon={Settings} label="Settings" onClick={() => navigate('/dispatch/settings')} last />
        </div>
        <p style={{ fontSize: 13, color: T.moonFaint, margin: '16px 0 12px' }}>Your profile didn't load.</p>
        <DispatchButton variant="main" data-testid="account-retry" onClick={fetchProfile}>Try again</DispatchButton>
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: T.night, minHeight: '100%', paddingLeft: px, paddingRight: px, paddingTop: 16, paddingBottom: 32, color: T.moon, fontFamily: F.heading }}>
      <p style={{ fontSize: 26, letterSpacing: -0.4, marginBottom: 28 }}>Me</p>

      {/* Identity */}
      {profile && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 28 }}>
          <div style={{ width: 60, height: 60, borderRadius: 30, backgroundColor: T.nightDeep, ...bd(T.nightHairS), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <span style={{ fontFamily: F.mono, fontSize: 18, color: T.moon, letterSpacing: 1 }}>{initials(profile.name)}</span>
          </div>
          <div style={{ flex: 1 }}>
            <p style={{ fontSize: 18, color: T.moon, fontFamily: F.heading, margin: '0 0 3px 0' }}>{profile.name}</p>
            <p style={{ fontSize: 13, fontWeight: 500, color: T.moonMute, margin: '0 0 3px 0' }}>{profile.title || profile.role}</p>
            <p style={{ fontSize: 12, color: T.moon, margin: '0 0 3px 0' }}>{profile.payout_label || (profile.payout_model === 'owner_full' ? 'Full package · $0 deduction' : 'Fixed rate card')}</p>
            <p style={{ fontSize: 12, color: T.moonFaint, margin: 0 }}>{profile.email}</p>
          </div>
        </div>
      )}
      {profile && (
        <div style={{ marginBottom: 28 }}>
          <DispatchButton variant="main" full icon={Pencil} data-testid="account-edit" onClick={() => navigate('/dispatch/profile-edit')}>Edit profile</DispatchButton>
        </div>
      )}

      {/* Stats rail */}
      {profile && (
        <div style={{ display: 'flex', backgroundColor: T.nightSoft, ...bd(T.nightHair), borderRadius: 2, marginBottom: 28 }}>
          {[
            { Icon: Briefcase, val: profile.total_jobs_completed, label: 'Jobs' },
            { Icon: Star,      val: profile.rating?.toFixed(1),   label: 'Rating' },
            { Icon: User,      val: fmtMemberSince(profile.member_since), label: 'Since' },
          ].map(({ Icon, val, label }, i) => (
            <div key={label} style={{ flex: 1, padding: '16px 12px', ...(i > 0 ? { borderLeft: `1px solid ${T.nightHair}` } : {}) }}>
              <div style={{ marginBottom: 8 }}><Icon size={13} color={T.moonMute} strokeWidth={1.5} /></div>
              <p style={{ fontFamily: F.mono, fontSize: 15, color: T.moon, margin: '0 0 4px 0' }}>{val}</p>
              <p style={{ fontFamily: F.heading, fontSize: 12, color: T.moonFaint, margin: 0 }}>{label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Account */}
      <div style={{ marginBottom: 24 }}>
        <p style={{ fontSize: 13, fontWeight: 500, color: T.moonFaint, marginBottom: 12 }}>Account</p>
        <div style={{ backgroundColor: T.nightSoft, ...bd(T.nightHair), borderRadius: 2 }}>
          <MenuRow Icon={Settings} label="Settings" sub="Passkeys, preferences, and more" onClick={() => navigate('/dispatch/settings')} last />
        </div>
      </div>

      {/* Availability toggle */}
      <div style={{ marginBottom: 24 }}>
        <p style={{ fontSize: 13, fontWeight: 500, color: T.moonFaint, marginBottom: 12 }}>Status</p>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', backgroundColor: T.nightSoft, ...bd(T.nightHair), borderRadius: 2, padding: '14px 16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 7, height: 7, borderRadius: 3.5, backgroundColor: availability ? T.ok : T.moonFaint }} />
            <span style={{ fontSize: 14, color: T.moon }}>{availability ? 'Available for jobs' : 'Not available'}</span>
          </div>
          <label style={{ position: 'relative', display: 'inline-block', width: 44, height: 24, flexShrink: 0 }}>
            <input
              type="checkbox"
              checked={availability}
              disabled={togglingAvail}
              onChange={e => toggleAvailability(e.target.checked)}
              style={{ opacity: 0, width: 0, height: 0 }}
            />
            <span style={{
              position: 'absolute', cursor: togglingAvail ? 'not-allowed' : 'pointer',
              top: 0, left: 0, right: 0, bottom: 0, borderRadius: 24,
              backgroundColor: availability ? T.ok + '99' : T.nightDeep,
              transition: 'background-color 0.2s',
            }}>
              <span style={{
                position: 'absolute', content: '', height: 18, width: 18,
                left: availability ? 22 : 3, bottom: 3, backgroundColor: availability ? T.ok : T.moonFaint,
                borderRadius: '50%', transition: 'left 0.2s',
              }} />
            </span>
          </label>
        </div>
      </div>

      {/* Earnings export */}
      <div style={{ marginBottom: 24 }}>
        <p style={{ fontSize: 13, fontWeight: 500, color: T.moonFaint, marginBottom: 12 }}>Earnings</p>
        <div className="bx-tap" role="link" onClick={() => navigate('/dispatch/payouts')} style={{ backgroundColor: T.nightSoft, ...bd(T.nightHair), borderRadius: 2, cursor: 'pointer' }}>
          <div style={{ display: 'flex', alignItems: 'center', padding: '14px 16px', gap: 12 }}>
            <div style={{ width: 28, display: 'flex', justifyContent: 'center' }}>
              <Download size={15} color={T.moonMute} strokeWidth={1.5} />
            </div>
            <div style={{ flex: 1 }}>
              <p style={{ fontSize: 14, color: T.moon, margin: '0 0 2px 0' }}>Payouts</p>
              <p style={{ fontSize: 11, color: T.moonFaint, margin: 0 }}>Payout history and export</p>
            </div>
            <ChevronRight size={14} color={T.moonFaint} strokeWidth={1.5} />
          </div>
        </div>
      </div>

      {/* Sign out */}
      <div style={{ marginBottom: 24 }}>
        {confirmSignOut ? (
          <div style={{ backgroundColor: T.nightSoft, ...bd(T.nightHair), borderRadius: 2, padding: '14px 16px' }}>
            <p style={{ fontSize: 14, color: T.moon, marginBottom: 12 }}>Sign out of Bricks?</p>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="bx-tap" onClick={() => setConfirmSignOut(false)} style={{ flex: 1, minHeight: 44, padding: '10px 0', backgroundColor: 'transparent', ...bd(T.nightHairS), borderRadius: 2, color: T.moonSoft, cursor: 'pointer', fontSize: 13 }}>
                Cancel
              </button>
              <button className="bx-tap" onClick={() => logout()} style={{ flex: 1, minHeight: 44, padding: '10px 0', backgroundColor: T.bad + '33', ...bd(T.bad + '66'), borderRadius: 2, color: T.badText, cursor: 'pointer', fontSize: 13 }}>
                Sign out
              </button>
            </div>
          </div>
        ) : (
          <button
            className="bx-tap"
            onClick={() => setConfirmSignOut(true)}
            style={{ display: 'flex', alignItems: 'center', gap: 10, backgroundColor: T.nightSoft, ...bd(T.nightHair), borderRadius: 2, padding: '14px 16px', width: '100%', cursor: 'pointer' }}
          >
            <LogOut size={15} color={T.badText} strokeWidth={1.5} />
            <span style={{ fontSize: 14, color: T.badText }}>Sign out</span>
          </button>
        )}
      </div>
    </div>
  );
}
